import { NeoniApi } from '../../lib/api'
import { FetchStatus } from '../../types/LoadingStates'
import { PaginationMeta } from '../../types/PaginationMeta'
import { PageGranularityOption, PAGE_GRANULARITY_LABELS } from '../../types/PageGranularity'
import { TableColumn, TableData } from '../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { SortConfig } from '../../components/BaseComponents/Datagrid/useSortableData'
import {
	getPurchases,
	GET_PURCHASES_CARBON_ACCOUNTS_LIMIT,
} from '../../redux/actions/carbonAccountsPurchasesActions'
import {
	selectAllpurchasesCarbonData,
	selectpurchasesCarbonDataError,
	selectpurchasesCarbonDataPagination,
	selectpurchasesCarbonDataStatus,
	purchasesCarbonData_RESET,
} from '../../redux/slices/carbonAccountsPurchasesSlice'
import {
	getPurchasesSuppliers,
	GET_PURCHASES_SUPPLIERS_LIMIT,
} from '../../redux/actions/carbonAccountsSupplierActions'
import {
	selectAllsuppliersCarbonData,
	selectsuppliersCarbonDataError,
	selectsuppliersCarbonDataPagination,
	selectsuppliersCarbonDataStatus,
	suppliersCarbonData_RESET,
} from '../../redux/slices/carbonAccountsSupplier'
import { constructPurchaseData, constructSupplierData, purchaseColumns, supplierColumns } from './carbonAccountsColumns'

export type CarbonAccountsGranularityId = 'suppliers' | 'items'

export type CarbonAccountsFetchParams = {
	companyId: string
	accountingPeriodId: string
	page: number
	limit?: number
	orderBy?: string
	direction?: string
	scope?: string
	q?: string
}

/** Context the registry's `buildColumns` needs to close over - shared across both granularities. */
export type CarbonAccountsColumnsContext = {
	navigate: (path: string, options?: { state?: string }) => void
	formatCurrency: (value: number) => string
	onResendInvite: (invitationId: string, supplierName: string) => void
}

export type CarbonAccountsGranularity = {
	option: PageGranularityOption<CarbonAccountsGranularityId>
	fetch: (api: NeoniApi, params: CarbonAccountsFetchParams) => any
	limit: number
	selectStatus: (state: any) => FetchStatus
	selectError: (state: any) => string | undefined
	selectData: (state: any) => any[]
	selectPagination: (state: any) => PaginationMeta
	/** Dispatched for the *outgoing* granularity when the user switches away from it. */
	reset: () => { type: string }
	buildColumns: (ctx: CarbonAccountsColumnsContext) => TableColumn[]
	toRows: (data: any[]) => TableData[]
	search: { name: string; label: string; placeholder: string }
	defaultSort: SortConfig
}

// Module-level constant so each granularity's selector functions keep a stable identity across
// renders - required for useAppSelector(carbonAccountsGranularities[granularity].selectData) to
// behave like any other selector rather than being treated as a new function every render.
export const carbonAccountsGranularities: Record<CarbonAccountsGranularityId, CarbonAccountsGranularity> = {
	suppliers: {
		option: { id: 'suppliers', label: PAGE_GRANULARITY_LABELS.suppliers },
		fetch: getPurchasesSuppliers,
		limit: GET_PURCHASES_SUPPLIERS_LIMIT,
		selectStatus: selectsuppliersCarbonDataStatus,
		selectError: selectsuppliersCarbonDataError,
		selectData: selectAllsuppliersCarbonData,
		selectPagination: selectsuppliersCarbonDataPagination,
		reset: suppliersCarbonData_RESET,
		buildColumns: ({ navigate, formatCurrency, onResendInvite }) => supplierColumns(navigate, formatCurrency, onResendInvite),
		toRows: constructSupplierData,
		search: { name: 'searchSuppliers', label: 'Search suppliers', placeholder: 'Search suppliers' },
		defaultSort: { key: 'totalEmissions', direction: 'descending' },
	},
	items: {
		option: { id: 'items', label: PAGE_GRANULARITY_LABELS.items },
		fetch: getPurchases,
		limit: GET_PURCHASES_CARBON_ACCOUNTS_LIMIT,
		selectStatus: selectpurchasesCarbonDataStatus,
		selectError: selectpurchasesCarbonDataError,
		selectData: selectAllpurchasesCarbonData,
		selectPagination: selectpurchasesCarbonDataPagination,
		reset: purchasesCarbonData_RESET,
		buildColumns: ({ navigate, formatCurrency }) => purchaseColumns((path) => navigate(path), formatCurrency),
		toRows: constructPurchaseData,
		search: { name: 'filterPurchases', label: 'Search items', placeholder: 'Search items' },
		defaultSort: { key: 'totalEmissions', direction: 'descending' },
	},
}
